import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { DownloadProgressBar } from "@/components/DownloadProgress";
import {
  LayoutDashboard,
  Building2,
  CalendarDays,
  CalendarCheck,
  ReceiptText,
  FolderOpen,
  Megaphone,
  FileText,
  Users,
  UserCog,
  ClipboardList,
  QrCode,
  Hotel,
  Calculator,
  Send,
  ScanLine,
  LogOut,
  Menu,
  X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, isAdmin, isMaster, isAgent, hasRight, canUseQuotes, canViewWebCheckins, canAssignRights, canAcquisition, canChangeAgentRates, storageGet } from "@/lib/api";
import { usePolling } from "@/lib/usePolling";
import { Wordmark } from "@/components/Brand";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { OfflineBanner } from "@/components/OfflineBanner";
import { InstallButton } from "@/components/InstallButton";
import { NotificationToggle } from "@/components/NotificationToggle";
import { toast } from "sonner";
import { draftCount, syncDrafts } from "@/lib/drafts";
import { formCount, syncForms } from "@/lib/offlineForms";

const NAV = [
  { to: "/properties", label: "Properties", icon: Building2, show: () => true },
  { to: "/", label: "Overview", icon: LayoutDashboard, end: true, show: (a) => isAdmin(a) },
  { to: "/occupancy", label: "Occupancy", icon: CalendarDays, show: (a) => isAgent(a) || hasRight(a, "occupancy.view") },
  { to: "/day-occupancy", label: "Day Occupancy", icon: ClipboardList, show: (a) => isAdmin(a) },
  { to: "/bookings", label: "Make Booking", icon: CalendarCheck, show: (a) => isAgent(a) },
  { to: "/bookings", label: "Make Booking", icon: CalendarCheck, show: (a) => hasRight(a, "bookings.view") },
  { to: "/quotes", label: "Create Quote", icon: ReceiptText, show: (a) => canUseQuotes(a) },
  { to: "/operations", label: "Operations", icon: ClipboardList, show: (a) => isAdmin(a) },
  { to: "/web-checkins", label: "Web Check-in", icon: QrCode, show: (a) => canViewWebCheckins(a) },
  { to: "/marketing", label: "Marketing", icon: FolderOpen, show: (a) => isAgent(a) || hasRight(a, "marketing.view") },
  { to: "/promotions", label: "Promotions", icon: Megaphone, show: (a) => isAgent(a) || hasRight(a, "promotions.view") },
  { to: "/rates", label: "Rate Sheets", icon: FileText, show: (a) => isAgent(a) || hasRight(a, "rates.view") },
  { to: "/ujhi", label: "UJHI Villas", icon: Hotel, img: "/ujhi-mark.png", show: (a) => hasRight(a, "ujhi.manage") },
  { to: "/acquisition", label: "Acquisition Analysis", icon: Calculator, show: (a) => canAcquisition(a) },
  { to: "/agents", label: "Agent Directory", icon: Users, show: (a) => isMaster(a) || canChangeAgentRates(a) },
  { to: "/agent-names", label: "Agents", icon: Users, show: (a) => !isMaster(a) && hasRight(a, "agents.view") },
  { to: "/agent-onboarding", label: "Agent Onboarding", icon: Send, show: (a) => isAdmin(a) },
  { to: "/staff", label: "Staff & Access", icon: UserCog, show: (a) => canAssignRights(a) },
  { to: "/store", label: "Store & Purchase", icon: ScanLine, show: (a) => isMaster(a) || hasRight(a, "store.view") },
];

export const AppLayout = () => {
  const { auth, logout, refreshMe } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  // Keep permissions fresh: refresh on mount, on route change, and when the tab
  // regains focus — so newly granted rights take effect without re-login.
  useEffect(() => {
    if (!isAdmin(auth)) return;
    refreshMe();
    const onFocus = () => refreshMe();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const items = NAV.filter((n) => n.show(auth));
  const displayName = isAdmin(auth)
    ? auth?.profile?.name || auth?.profile?.email
    : auth?.profile?.firmName || auth?.profile?.ownerName;
  const roleLabel = isAdmin(auth)
    ? `Central Office · ${auth?.profile?.permission}`
    : "Travel Agent";
  const current = items.find((i) => (i.end ? location.pathname === i.to : location.pathname.startsWith(i.to)));

  const [unread, setUnread] = useState(0);
  const computeUnread = async () => {
    if (!canViewWebCheckins(auth)) return;
    try {
      const r = await api.get("/checkin/list");
      const rows = r.data || [];
      const seen = storageGet(localStorage, "checkinsLastSeen") || "";
      setUnread(rows.filter((x) => (x.createdAt || "") > seen).length);
    } catch { /* ignore */ }
  };
  usePolling(computeUnread, 30000, [auth]);
  useEffect(() => {
    const onSeen = () => computeUnread();
    window.addEventListener("checkins-seen", onSeen);
    return () => window.removeEventListener("checkins-seen", onSeen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  const [cancelReqs, setCancelReqs] = useState(0);
  const [payPending, setPayPending] = useState(0);
  usePolling(async () => {
    if (!hasRight(auth, "bookings.view")) return;
    try {
      const r = await api.get("/bookings", { params: { status: "confirmed" } });
      const rows = r.data || [];
      setCancelReqs(rows.filter((b) => b.cancellationRequested).length);
      setPayPending(rows.filter((b) => b.paymentStatus === "submitted").length);
    } catch { /* ignore */ }
  }, 30000, [auth]);

  // Offline queues: show pending counts and auto-submit when back online
  // (booking drafts + web check-ins + payment proofs).
  const [drafts, setDrafts] = useState(draftCount());
  const [offForms, setOffForms] = useState(formCount());
  useEffect(() => {
    const refresh = () => { setDrafts(draftCount()); setOffForms(formCount()); };
    const trySync = async () => {
      if (!navigator.onLine) return;
      const r = await syncDrafts();
      if (r.sent) toast.success(`${r.sent} offline booking${r.sent > 1 ? "s" : ""} submitted now you're back online`);
      const rf = await syncForms();
      if (rf.sent) toast.success(`${rf.sent} offline submission${rf.sent > 1 ? "s" : ""} synced now you're back online`);
      refresh();
      if (r.sent || rf.sent) window.dispatchEvent(new Event("bookings-synced"));
    };
    window.addEventListener("drafts-changed", refresh);
    window.addEventListener("offline-forms-changed", refresh);
    window.addEventListener("online", trySync);
    trySync();
    return () => {
      window.removeEventListener("drafts-changed", refresh);
      window.removeEventListener("offline-forms-changed", refresh);
      window.removeEventListener("online", trySync);
    };
  }, []);

  return (
    <div className="flex h-screen w-full overflow-hidden bg-white font-body text-slate-900">
      <DownloadProgressBar />
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)} data-testid="sidebar-backdrop" className="fixed inset-0 z-40 bg-black/40 lg:hidden" />
      )}
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 flex w-[240px] flex-shrink-0 flex-col border-r border-slate-200 bg-white transition-transform duration-200 lg:static lg:z-auto lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-5">
          <Wordmark size="sm" />
          <button onClick={() => setSidebarOpen(false)} className="text-slate-400 hover:text-slate-900 lg:hidden" data-testid="sidebar-close" aria-label="Close menu"><X size={20} /></button>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto p-3">
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.label}
                to={item.to}
                end={item.end}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-sm px-3 py-2.5 text-sm transition-colors ${
                    isActive
                      ? "bg-slate-900 font-medium text-white"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`
                }
              >
                {item.img ? <img src={item.img} alt="" className="h-[18px] w-[18px] shrink-0 rounded-[3px] object-contain" /> : <Icon size={17} />}
                <span className="flex-1">{item.label}</span>
                {item.to === "/web-checkins" && unread > 0 && (
                  <span data-testid="webcheckin-unread" className="rounded-full bg-[#E63946] px-1.5 py-0.5 text-[10px] font-semibold text-white">{unread}</span>
                )}
                {item.to === "/bookings" && hasRight(auth, "bookings.view") && cancelReqs > 0 && (
                  <span data-testid="bookings-cancelreq-count" title="Cancellation requests awaiting action" className="rounded-full bg-orange-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">{cancelReqs}</span>
                )}
                {item.to === "/bookings" && hasRight(auth, "bookings.view") && payPending > 0 && (
                  <span data-testid="bookings-paypending-count" title="Payments awaiting confirmation" className="rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">{payPending}</span>
                )}
                {item.to === "/bookings" && (drafts + offForms) > 0 && (
                  <span data-testid="bookings-drafts-count" title="Offline submissions waiting to sync" className="rounded-full bg-sky-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">{drafts + offForms}</span>
                )}
              </NavLink>
            );
          })}
        </nav>
        <div className="border-t border-slate-200 p-3">
          <div className="rounded-sm bg-slate-50 p-3">
            <div className="truncate text-sm font-medium text-slate-900" data-testid="sidebar-user-name">
              {displayName}
            </div>
            <div className="text-xs capitalize text-slate-500">{roleLabel}</div>
            <button
              onClick={() => {
                logout();
                navigate("/login");
              }}
              data-testid="logout-button"
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-sm border border-slate-200 py-2 text-xs font-medium text-slate-600 transition-colors hover:border-[#E63946] hover:text-[#E63946]"
            >
              <LogOut size={14} /> Sign out
            </button>
            <NotificationToggle className="mt-2" />
            <InstallButton compact className="mt-2 w-full" testid="sidebar-install-btn" />
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <OfflineBanner />
        <header className="flex h-16 flex-shrink-0 items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 sm:px-6 lg:px-8">
          <div className="flex min-w-0 items-center gap-3">
            <button onClick={() => setSidebarOpen(true)} className="rounded-sm border border-slate-200 p-2 text-slate-600 hover:bg-slate-50 lg:hidden" data-testid="sidebar-toggle" aria-label="Open menu"><Menu size={18} /></button>
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-400">Agent Portal</div>
              <h1 className="truncate font-display text-lg text-slate-900 sm:text-xl" data-testid="page-title">
                {current?.label || "Overview"}
              </h1>
            </div>
          </div>
          <div className="hidden items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-400 sm:flex">
            SWAGAT <span className="text-[#E63946]">&#10084;</span> SE
          </div>
        </header>
        <main className="flex-1 overflow-auto bg-[#FAFAFA] p-4 sm:p-6 lg:p-8">
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
};
